import { jsx } from "react/jsx-runtime";
import { createContext, useDeferredValue, useEffect, useLayoutEffect } from "react";
import classnames from "classnames";
import { useActiveSubmenu, useMainMenuTheme, useMainMenuUrls, useMediaQuery, useSearchTerm, useUserWithRoleAndPermissions, useVisibleHeight } from "./hooks/index.js";
const MainMenuContext = /*#__PURE__*/ createContext(void 0);
const MainMenuProvider = ({ children, customUrls, customThemes, theme: themeProp, isTransparent, isBlurred, position = 'relative', countryPrefix, initialSearchTerm, layoutConfig, amplitudePageName, topBannerIsOpen, organizationSlug, centralizeOrganizationId, ...mainMenuProps })=>{
    const { matches: matchesSm } = useMediaQuery('sm');
    const { matches: matchesMd } = useMediaQuery('md');
    const { matches: matchesLg } = useMediaQuery('lg');
    const deferredMatchesSm = useDeferredValue(matchesSm);
    let currentBreakpointKey = 'xs';
    if (matchesSm) currentBreakpointKey = 'sm';
    if (matchesMd) currentBreakpointKey = 'md';
    if (matchesLg) currentBreakpointKey = 'lg';
    const { me, loading } = useUserWithRoleAndPermissions();
    const searchTermProps = useSearchTerm({
        initialSearchTerm
    });
    const urls = useMainMenuUrls({
        customUrls,
        me,
        countryPrefix,
        amplitudePageName
    });
    const activeSubmenuProps = useActiveSubmenu({
        amplitudePageName,
        onLeftMenuClick: mainMenuProps.onLeftMenuClick,
        onChangeActiveSubmenu: mainMenuProps.onChangeActiveSubmenu
    });
    const searchOverlayIsOpen = 'search' === activeSubmenuProps.activeSubMenu && ('md' === currentBreakpointKey || 'lg' === currentBreakpointKey);
    const { theme, themeName } = useMainMenuTheme({
        customThemes,
        theme: themeProp,
        isTransparent,
        searchOverlayIsOpen
    });
    const mainMenuVisibilityProps = useVisibleHeight({
        position,
        theme,
        topBannerIsOpen
    });
    useEffect(()=>{
        if (!isTransparent) if (activeSubmenuProps.activeSubMenu) document.body.className = classnames(document.body.className, theme.nav.backgroundColorClassName);
        else document.body.className = document.body.className.replace(theme.nav.backgroundColorClassName, '');
    }, [
        activeSubmenuProps.activeSubMenu,
        theme.nav.backgroundColorClassName,
        isTransparent
    ]);
    useLayoutEffect(()=>{
        const hasActiveSubmenuOpen = !!activeSubmenuProps.activeSubMenu;
        if (hasActiveSubmenuOpen && deferredMatchesSm && !matchesSm) activeSubmenuProps.onCloseSubmenu();
    }, [
        activeSubmenuProps,
        activeSubmenuProps.activeSubMenu,
        deferredMatchesSm,
        matchesSm
    ]);
    return /*#__PURE__*/ jsx(MainMenuContext.Provider, {
        value: {
            ...mainMenuProps,
            loadingMe: loading,
            me,
            urls: {
                ...urls,
                searchLanding: `${urls.searchLanding}?q=${searchTermProps.searchTerm.cleanValue}`
            },
            theme,
            themeName,
            isTransparent,
            isBlurred,
            currentBreakpointKey,
            position,
            countryPrefix,
            amplitudePageName,
            layoutConfig: {
                hideInterestsButton: true,
                hideNightSwitch: true,
                hideMicroDegrees: 'br' === countryPrefix || layoutConfig?.hideMicroDegrees,
                hideHigherEdLink: true,
                phoneInputProps: {
                    hide: true,
                    source: ''
                },
                hideMentorLandingLink: !!me?.isPremium,
                hideDownloadableResouces: true,
                hideBlogCategories: true,
                hideSocialLinks: true,
                hideBlogSearch: true,
                hideDesktopBlogLeftMenu: true,
                hideSolutionsMenu: true,
                hideCrehanaCoinsButton: true,
                ...layoutConfig
            },
            organizationSlug,
            centralizeOrganizationId,
            initialSearchTerm,
            ...mainMenuVisibilityProps,
            ...searchTermProps,
            ...activeSubmenuProps
        },
        children: children
    });
};
export { MainMenuContext, MainMenuProvider };
